import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import { useCallback, useMemo } from 'react';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { ACADEMY_EXERCISE_CONTEXT_KEY } from 'navigation-components/config';
const ContextStoreDefaultState = {
  context: null
};
const RunningStoreDefaultState = {
  isRunning: false,
  isPartiallyCompleted: false
};
const RUNNING_STORE_PERSIST_NAME = 'academy:practical-exercise-running';
const COMPLETED_EXERCISE_EVENTS_STORE_NAME = 'academy:completed-exercise-events';
const CompletedExerciseEventsDefaultState = {
  byContentId: {}
};
export const useCompletedExerciseEventsStore = create()(persist(set => Object.assign({}, CompletedExerciseEventsDefaultState, {
  addCompletedEvent: (contentId, eventId, label) => {
    set(s => {
      var _s$byContentId$key;
      const key = contentId;
      const prevMap = (_s$byContentId$key = s.byContentId[key]) !== null && _s$byContentId$key !== void 0 ? _s$byContentId$key : {};
      if (prevMap[eventId]) {
        return s;
      }
      return {
        byContentId: Object.assign({}, s.byContentId, {
          [key]: Object.assign({}, prevMap, {
            [eventId]: label
          })
        })
      };
    });
  },
  clearCompletedEventsForContent: contentId => {
    set(s => {
      const key = contentId;
      if (!(key in s.byContentId)) {
        return s;
      }
      const _s$byContentId = s.byContentId,
        rest = _objectWithoutPropertiesLoose(_s$byContentId, [key].map(_toPropertyKey));
      return {
        byContentId: rest
      };
    });
  }
}), {
  name: COMPLETED_EXERCISE_EVENTS_STORE_NAME,
  storage: createJSONStorage(() => sessionStorage),
  partialize: state => ({
    byContentId: state.byContentId
  })
}));
const useExerciseContextStore = create()(persist(set => Object.assign({}, ContextStoreDefaultState, {
  setContext: context => set({
    context
  })
}), {
  name: ACADEMY_EXERCISE_CONTEXT_KEY,
  storage: createJSONStorage(() => localStorage)
}));
const useExerciseRunningStore = create()(persist(set => Object.assign({}, RunningStoreDefaultState, {
  setIsRunning: isRunning => set({
    isRunning
  }),
  setIsPartiallyCompleted: isPartiallyCompleted => set({
    isPartiallyCompleted
  })
}), {
  name: RUNNING_STORE_PERSIST_NAME,
  storage: createJSONStorage(() => sessionStorage)
}));

// Same-origin iframes (e.g. toolbar vs instructions) each own Zustand; another
// frame’s persist write does not update this one until we rehydrate on `storage`
// (localStorage = context, sessionStorage = running/partial).
// No removeEventListener: module loads once for the page lifetime and we intentionally keep syncing.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', event => {
    if (event.key === RUNNING_STORE_PERSIST_NAME) {
      void useExerciseRunningStore.persist.rehydrate();
    }
    if (event.key === ACADEMY_EXERCISE_CONTEXT_KEY) {
      void useExerciseContextStore.persist.rehydrate();
    }
    if (event.key === COMPLETED_EXERCISE_EVENTS_STORE_NAME) {
      void useCompletedExerciseEventsStore.persist.rehydrate();
    }
  });
}
export const usePracticalExerciseStore = () => {
  const {
    context,
    setContext
  } = useExerciseContextStore();
  const {
    isRunning,
    setIsRunning,
    isPartiallyCompleted,
    setIsPartiallyCompleted
  } = useExerciseRunningStore();
  const startExerciseContext = useCallback(ctx => {
    setContext(ctx);
    setIsRunning(true);
    setIsPartiallyCompleted(false);
  }, [setContext, setIsRunning, setIsPartiallyCompleted]);
  const stopExerciseContext = useCallback(() => {
    if ((context === null || context === void 0 ? void 0 : context.id) != null) {
      useCompletedExerciseEventsStore.getState().clearCompletedEventsForContent(context.id);
    }
    setContext(null);
    setIsRunning(false);
    setIsPartiallyCompleted(false);
  }, [context, setContext, setIsRunning, setIsPartiallyCompleted]);
  const setPartiallyCompleted = useCallback(isPartiallyCompletedValue => setIsPartiallyCompleted(isPartiallyCompletedValue), [setIsPartiallyCompleted]);
  const resumeExerciseContext = useCallback(() => setIsRunning(true), [setIsRunning]);
  const status = useMemo(() => isPartiallyCompleted && context !== null ? 'PARTIALLY_COMPLETED' : isRunning && context !== null ? 'RUNNING' : context === null ? 'STOPPED' : 'SUSPENDED', [isPartiallyCompleted, isRunning, context]);
  return {
    context,
    status,
    startExerciseContext,
    stopExerciseContext,
    resumeExerciseContext,
    setPartiallyCompleted
  };
};
export const resetExerciseContextStore = () => useExerciseContextStore.setState(ContextStoreDefaultState);
export const resetExerciseRunningStore = () => useExerciseRunningStore.setState(RunningStoreDefaultState);
export const resetCompletedExerciseEventsStore = () => useCompletedExerciseEventsStore.setState(CompletedExerciseEventsDefaultState);